import { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Coins,
  Cpu,
  Filter,
  MessagesSquare,
  Search,
  User,
  Wallet,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Combobox, type ComboboxOption } from '@/shared/ui/combobox';
import { Input } from '@/shared/ui/input';
import { Pagination } from '@/shared/ui/pagination';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  useAiModels,
  useCostBreakdown,
  type BreakdownGroupBy,
  type BreakdownParams,
  type BreakdownRow as BreakdownRowData,
  type BreakdownSort,
} from '../api/use-ai-admin';
import { formatUsd, modelLabel } from '../model/format';

const PAGE_SIZE = 12;
const OPTIONS_LIMIT = 100;
const ALL = 'all';

type SegmentationFilter = typeof ALL | 'with' | 'without';

const SORT_LABELS: Record<BreakdownSort, string> = {
  costUsd: 'Maior gasto',
  segmentations: 'Mais segmentações',
  sessions: 'Mais conversas',
  costPerSegmentation: 'Maior custo por segmentação',
};

const SEARCH_PLACEHOLDER: Record<BreakdownGroupBy, string> = {
  model: 'Buscar modelo…',
  passbook: 'Buscar passbook…',
  user: 'Buscar por nome ou e-mail…',
};

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

function SummaryStat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-border/50 bg-foreground/[0.02] p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <p className="mt-1 truncate text-lg font-semibold tabular-nums">{value}</p>
      {hint ? <p className="truncate text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function BreakdownRow({
  row,
  label,
  total,
}: {
  row: BreakdownRowData;
  label: string;
  total: number;
}) {
  const share = total > 0 ? (row.costUsd / total) * 100 : 0;
  const perSegmentation = row.segmentations > 0 ? row.costUsd / row.segmentations : null;
  return (
    <li className="min-w-0 space-y-2 rounded-xl border border-border/40 p-3 sm:p-3.5">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">{label}</span>
          {row.sublabel ? (
            <span className="block truncate text-xs text-muted-foreground">{row.sublabel}</span>
          ) : null}
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-sm font-semibold tabular-nums">{formatUsd(row.costUsd)}</span>
          <span className="block text-xs tabular-nums text-muted-foreground">
            {share.toFixed(share < 1 ? 1 : 0)}% do gasto
          </span>
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted-foreground/15">
        <div
          className="h-full rounded-full bg-primary/80"
          style={{ width: `${Math.max(share, 1.5)}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground/85">
          {plural(row.segmentations, 'segmentação criada', 'segmentações criadas')}
        </span>
        <span>{plural(row.sessions, 'conversa', 'conversas')}</span>
        <span>{plural(row.calls, 'chamada', 'chamadas')}</span>
        {perSegmentation !== null ? (
          <span>{formatUsd(perSegmentation)} por segmentação</span>
        ) : null}
      </div>
    </li>
  );
}

/** Para onde foi o gasto do mês, com filtros, e quanto dele virou segmentação salva. */
export function CostBreakdownCard({ month }: { month: string }) {
  const [groupBy, setGroupBy] = useState<BreakdownGroupBy>('model');
  const [sort, setSort] = useState<BreakdownSort>('costUsd');
  const [model, setModel] = useState(ALL);
  const [idPassbook, setIdPassbook] = useState(ALL);
  const [segmentation, setSegmentation] = useState<SegmentationFilter>(ALL);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(
    () => setPage(0),
    [month, groupBy, sort, model, idPassbook, segmentation, debouncedSearch],
  );

  const params: BreakdownParams = {
    month,
    groupBy,
    sort,
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    model: groupBy !== 'model' && model !== ALL ? model : undefined,
    idPassbook: groupBy !== 'passbook' && idPassbook !== ALL ? idPassbook : undefined,
    segmentation: segmentation === ALL ? undefined : segmentation,
  };
  const { data, isLoading, isFetching } = useCostBreakdown(params);
  const { data: catalog } = useAiModels();

  const optionsBase = { month, sort: 'costUsd' as const, page: 0, limit: OPTIONS_LIMIT };
  const { data: modelsUsed } = useCostBreakdown({ ...optionsBase, groupBy: 'model' });
  const { data: passbooksUsed } = useCostBreakdown({ ...optionsBase, groupBy: 'passbook' });

  const modelOptions = useMemo<ComboboxOption[]>(
    () => [
      { value: ALL, label: 'Todos os modelos' },
      ...(modelsUsed?.rows ?? []).map((row) => ({
        value: row.key,
        label: modelLabel(row.key, catalog?.models),
        description: row.key,
      })),
    ],
    [modelsUsed, catalog],
  );
  const passbookOptions = useMemo<ComboboxOption[]>(
    () => [
      { value: ALL, label: 'Todos os passbooks' },
      ...(passbooksUsed?.rows ?? []).map((row) => ({ value: row.key, label: row.label })),
    ],
    [passbooksUsed],
  );

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const segmentations = summary?.segmentations ?? 0;
  const sessions = summary?.sessions ?? 0;
  const filtered =
    Boolean(debouncedSearch) ||
    (groupBy !== 'model' && model !== ALL) ||
    (groupBy !== 'passbook' && idPassbook !== ALL) ||
    segmentation !== ALL;

  const clearFilters = () => {
    setSearch('');
    setModel(ALL);
    setIdPassbook(ALL);
    setSegmentation(ALL);
  };

  const rowLabel = (row: BreakdownRowData) =>
    groupBy === 'model' ? modelLabel(row.key, catalog?.models) : row.label;

  return (
    <Card>
      <CardHeader className="gap-3 md:flex-row md:items-start md:justify-between md:space-y-0">
        <div className="space-y-1">
          <CardTitle>Onde o dinheiro foi</CardTitle>
          <CardDescription>
            Custo real por modelo, passbook ou usuário, e quanto virou segmentação salva.
          </CardDescription>
        </div>
        <SegmentedControl
          size="sm"
          fill
          value={groupBy}
          onChange={setGroupBy}
          className="shrink-0 md:w-auto"
          options={[
            { value: 'model', label: 'Por modelo', icon: Cpu },
            { value: 'passbook', label: 'Por passbook', icon: Building2 },
            { value: 'user', label: 'Por usuário', icon: User },
          ]}
        />
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="relative min-w-0 sm:min-w-56 sm:flex-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={SEARCH_PLACEHOLDER[groupBy]}
              className="pl-9"
              aria-label="Buscar"
            />
            <Search className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          {groupBy !== 'model' ? (
            <div className="sm:w-52">
              <Combobox
                value={model}
                onChange={setModel}
                options={modelOptions}
                searchPlaceholder="Buscar modelo"
              />
            </div>
          ) : null}
          {groupBy !== 'passbook' ? (
            <div className="sm:w-52">
              <Combobox
                value={idPassbook}
                onChange={setIdPassbook}
                options={passbookOptions}
                searchPlaceholder="Buscar passbook"
              />
            </div>
          ) : null}
          <Select
            value={segmentation}
            onValueChange={(v) => setSegmentation(v as SegmentationFilter)}
          >
            <SelectTrigger aria-label="Conversas" className="sm:w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas as conversas</SelectItem>
              <SelectItem value="with">Que criaram segmentação</SelectItem>
              <SelectItem value="without">Sem segmentação</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => setSort(v as BreakdownSort)}>
            <SelectTrigger aria-label="Ordenar" className="sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SORT_LABELS) as BreakdownSort[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {SORT_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtered ? (
            <Button variant="ghost" onClick={clearFilters}>
              <X />
              Limpar filtros
            </Button>
          ) : null}
        </div>

        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <SummaryStat
              icon={Coins}
              label={filtered ? 'Gasto filtrado' : 'Gasto no mês'}
              value={formatUsd(summary?.costUsd ?? 0)}
              hint={plural(summary?.calls ?? 0, 'chamada ao modelo', 'chamadas ao modelo')}
            />
            <SummaryStat
              icon={Filter}
              label="Segmentações criadas"
              value={String(segmentations)}
              hint={`de ${plural(sessions, 'conversa', 'conversas')}`}
            />
            <SummaryStat
              icon={Wallet}
              label="Custo por segmentação"
              value={segmentations > 0 ? formatUsd((summary?.costUsd ?? 0) / segmentations) : '—'}
              hint="Gasto ÷ segmentações"
            />
            <SummaryStat
              icon={MessagesSquare}
              label="Sem segmentação"
              value={String(Math.max(sessions - segmentations, 0))}
              hint="Conversas que não salvaram"
            />
          </div>
        )}

        {isLoading ? (
          <div className="grid gap-2 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {filtered ? 'Nada encontrado com esses filtros.' : 'Nenhum uso neste mês.'}
          </p>
        ) : (
          <ul
            className={
              isFetching
                ? 'grid gap-2 opacity-70 transition-opacity lg:grid-cols-2'
                : 'grid gap-2 lg:grid-cols-2'
            }
          >
            {rows.map((row) => (
              <BreakdownRow
                key={row.key}
                row={row}
                label={rowLabel(row)}
                total={summary?.costUsd ?? 0}
              />
            ))}
          </ul>
        )}

        {total > PAGE_SIZE ? (
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        ) : null}
      </CardContent>
    </Card>
  );
}
